import { Button, Grid, Tooltip } from '@mui/material';
import moment from 'moment';
import React, { MouseEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Datatable, { PagingTypeEnum } from '../Datatable/Datatable';
import DatatableColumnModel from '../Datatable/DatatableColumnModel';
import { OrderDirection } from '../Datatable/OrderDirection';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import BaseResponse from '../../app/BaseResponse';
import Utility from '../../app/utility';
import _ from 'lodash';
import InvestmentModel from './models/InvestmentModel';
import Swal from 'sweetalert2';
import InvestmentApi, { DeleteInvestmentByIdParam } from './api/InvestmentApi';
import { InvestmentSearchSelector, setCheckedInvestments, setDatatableSelectedInvestmentIndex, setInvestmentSearchResults, setTriggerInvestmentSearch } from './reducers/InvestmentSearchReducer';
import { InvestmentInputSelector, setSelectedInvestmentId } from './reducers/InvestmentInputReducer';
import Api from '../../app/apiRequest';

const InvestmentTable: React.FunctionComponent = () => {
    let dispatch = useDispatch();

    let InvestmentData = useSelector(InvestmentSearchSelector);
    const InvestmentInputViewData = useSelector(InvestmentInputSelector);

    useEffect(() => {
        if (InvestmentData.searchResults.length == 0 || InvestmentData.triggerSearch > 0) {
            dispatch(showLoading(true));
            InvestmentApi.GetAll().then((response: BaseResponse<InvestmentModel[]>) => {
                if (response.success) {
                    dispatch(setInvestmentSearchResults(response.data));

                    dispatch(showLoading(false));

                    dispatch(setTriggerInvestmentSearch(0));
                } else {
                    Utility.Alert.Error(response.message);
                    dispatch(showLoading(false));
                }
            }).catch((error) => {
                Utility.Alert.Error(error);
                dispatch(showLoading(false));
            })
        }
    }, [InvestmentData.triggerSearch]);

    const columns = [
        {
            Field: Utility.propertyOf<InvestmentModel>("rate"),
            Title: "Rate",
            AllowSort: true,
            Render: (rowData: InvestmentModel) => (
                <>
                    {rowData.rate ? rowData.rate.toFixed(2) + "%" : "0%"}
                </>
            )
        } as DatatableColumnModel<InvestmentModel>,
        {
            Field: Utility.propertyOf<InvestmentModel>("investmentDate"),
            Title: "Investment Date",
            AllowSort: true,
            Render: (rowData: InvestmentModel) => (
                <>
                    {Utility.DateFormat.formatDateString(rowData.investmentDate).toUpperCase()}
                </>
            )
        } as DatatableColumnModel<InvestmentModel>,
        {
            Field: Utility.propertyOf<InvestmentModel>("items"),
            Title: "Items",
            AllowSort: true,
        } as DatatableColumnModel<InvestmentModel>,
        {
            Field: Utility.propertyOf<InvestmentModel>("type"),
            Title: "Type",
            AllowSort: true,
        } as DatatableColumnModel<InvestmentModel>,
        {
            Field: Utility.propertyOf<InvestmentModel>("amount"),
            Title: "Amount",
            AllowSort: true,
            Render: (rowData: InvestmentModel) => (
                <>
                    {Utility.NumberFormat.formatUSD(rowData.amount)}
                </>
            )
        } as DatatableColumnModel<InvestmentModel>,
        {
            Field: Utility.propertyOf<InvestmentModel>("settleDate"),
            Title: "Settle Date",
            AllowSort: true,
            Render: (rowData: InvestmentModel) => (
                <>
                    {Utility.DateFormat.formatDateString(rowData.settleDate).toUpperCase()}
                </>
            )
        } as DatatableColumnModel<InvestmentModel>,
        {
            Field: Utility.propertyOf<InvestmentModel>("interest"),
            Title: "Interest",
            AllowSort: true,
            Render: (rowData: InvestmentModel) => (
                <>
                    {rowData.interest ? rowData.interest.toFixed(2) + "%" : "0%"}
                </>
            )
        } as DatatableColumnModel<InvestmentModel>,
        {
            Field: Utility.propertyOf<InvestmentModel>("description"),
            Title: "Description",
            AllowSort: true,
        } as DatatableColumnModel<InvestmentModel>,
        {
            Field: "",
            Title: "",
            AllowSort: true,
            Render: (rowData: InvestmentModel, index: number) => (
                <>
                    <Tooltip title="Delete Investment">
                        <Button
                            className='table-action-button'
                            variant='outlined'
                            color='error'
                            onClick={() => {
                                deleteInvestment(rowData.id)
                            }}
                        >
                            <i className="fa fa-trash" aria-hidden="true"></i>
                        </Button>
                    </Tooltip>
                </>
            )
        } as DatatableColumnModel<InvestmentModel>,
    ];

    const deleteInvestment = (id: string) => {
        Swal.fire({
            title: 'Are you sure want to delete this market info? This action can not be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No'
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(showLoading(true));
                InvestmentApi.Delete({
                    id: id
                } as DeleteInvestmentByIdParam)
                    .then((response: BaseResponse<InvestmentModel>) => {
                        if (response.success) {
                            Utility.Alert.Success("Investment deleted successful");

                            dispatch(showLoading(false));

                            dispatch(setTriggerInvestmentSearch(new Date().getTime()));
                        }
                    });
            }
        })
    }

    const getTableClassName = () => {
        let className = "investment-datatable-search-result nice-scroll";

        if (!InvestmentInputViewData.InvestmentInputExpand) {
            className += " investment-datatable-search-result-quick-input-collapse-only";
        }

        return className;
    }

    return <div className='datatable-wrapper'>
        <Grid
            container
            justifyContent="flex-end"
            className="table-search-result-utility-bar"
        >
            <Button
                color="secondary"
                variant='outlined'
                className='table-search-result-utility-bar-item'
                onClick={() => {
                    let url = "/api/Investment/ExportExcel";

                    const newWindow = window.open(url, '_blank', 'noopener,noreferrer')
                    if (newWindow) newWindow.opener = null
                }}
            >Export Excel</Button>
        </Grid>
        <Datatable<InvestmentModel>
            useCheckBox={true}
            useDefaultSearchBar={false}
            pagingType={PagingTypeEnum.None}
            data={InvestmentData.searchResults}
            useItemPerPage={false}
            columns={columns}
            isDataLoaded={true}
            className={getTableClassName()}
            useHeaderSort={false}
            onRowSelectedChange={(Investment: InvestmentModel, index: number, event: MouseEvent<HTMLElement>) => {
                var target = event.target as HTMLElement;
                if (target.tagName !== "BUTTON") {
                    if (Investment.id && Investment.id.length > 0) {
                        dispatch(setSelectedInvestmentId(Investment.id))
                        dispatch(setDatatableSelectedInvestmentIndex(index));
                    }
                }
            }}
            selectedRowIndex={InvestmentData.datatableSelectedIndex}
            stickyHeader={true}
            onTableCheckboxSelectedChange={(selectedRows: InvestmentModel[]) => {
                dispatch(setCheckedInvestments(selectedRows));
            }}
        ></Datatable>
    </div>
};

export default InvestmentTable;